import React, { useEffect, useState, useRef } from "react";
import { ymd } from "../lib/dates";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Student, SignOut, Printer, WhatsappLogo, Wallet, Receipt, Warning, CheckCircle, Buildings, Phone, MapPin, Calendar, UploadSimple, Bank, PaperPlaneTilt, Clock, XCircle, PencilSimple, Trash } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { API, formatIDR, formatDate, formatMonth, formatApiError } from "../lib/api";
import { downloadReceipt, buildWhatsAppReceiptURL } from "../lib/receiptPdf";

const STATUS_META = {
  paid: { label: "LUNAS", cls: "bg-emerald-100 text-emerald-700" },
  partial: { label: "SEBAGIAN", cls: "bg-amber-100 text-amber-700" },
  unpaid: { label: "BELUM BAYAR", cls: "bg-rose-100 text-rose-700" },
};

const PENGAJUAN_STATUS = {
  pending: { label: "MENUNGGU REVIEW", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
  approved: { label: "DISETUJUI", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle },
  rejected: { label: "DITOLAK", cls: "bg-rose-100 text-rose-700 border-rose-300", icon: XCircle },
};

export default function PortalWali() {
  const nav = useNavigate();
  const [siswa, setSiswa] = useState(null);
  const [lembaga, setLembaga] = useState(null);
  const [tagihan, setTagihan] = useState(null);
  const [pembayaran, setPembayaran] = useState(null);
  const [tab, setTab] = useState("tagihan");
  const [settings, setSettings] = useState({});
  const [rekenings, setRekenings] = useState([]);
  const [pengajuans, setPengajuans] = useState([]);
  const [pengajuanOpen, setPengajuanOpen] = useState(false);
  const [pengajuanForm, setPengajuanForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const token = typeof window !== "undefined" ? localStorage.getItem("wali_token") : null;
  const cfg = { headers: { Authorization: `Bearer ${token}` } };

  const loadPengajuan = async () => {
    try {
      const [r, pg] = await Promise.all([
        api.get("/wali/rekening", cfg),
        api.get("/wali/pengajuan", cfg),
      ]);
      setRekenings(r.data); setPengajuans(pg.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!token) { nav("/portal-wali/login"); return; }
    (async () => {
      try {
        const [me, t, p, s] = await Promise.all([
          api.get("/wali/me", cfg),
          api.get("/wali/tagihan", cfg),
          api.get("/wali/pembayaran", cfg),
          api.get("/wali/settings", cfg).catch(() => ({ data: {} })),
        ]);
        setSiswa(me.data.siswa); setLembaga(me.data.lembaga);
        setTagihan(t.data); setPembayaran(p.data);
        setSettings(s.data);
        await loadPengajuan();
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("wali_token");
          localStorage.removeItem("wali_siswa");
          nav("/portal-wali/login");
        } else {
          toast.error("Gagal memuat data");
        }
      }
    })();
  }, [token, nav]);

  const openPengajuan = (t) => {
    setPengajuanForm({
      tagihan_id: t?.id || "",
      nomenklatur_code: t?.nomenklatur_code || "IPP",
      amount: t?.sisa || 0,
      payment_date: ymd(),
      rekening_id: rekenings[0]?.id || "",
      bukti_path: "",
      bukti_url: "",
      bukti_filename: "",
      catatan: "",
    });
    setPengajuanOpen(true);
  };
  const openEditPengajuan = (p) => {
    setPengajuanForm({
      _editing_id: p.id,
      tagihan_id: p.tagihan_id || "",
      nomenklatur_code: p.nomenklatur_code,
      amount: p.amount,
      payment_date: p.payment_date,
      rekening_id: p.rekening_id || "",
      bukti_path: p.bukti_path,
      bukti_url: p.bukti_url,
      bukti_filename: p.bukti_path?.split("/").pop() || "bukti",
      catatan: p.catatan || "",
    });
    setPengajuanOpen(true);
  };

  const uploadBukti = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error(`Ukuran ${(file.size/1024).toFixed(0)} KB melebihi 1 MB`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/wali/upload-bukti", fd, { ...cfg, headers: { ...cfg.headers, "Content-Type": "multipart/form-data" } });
      setPengajuanForm((f) => ({ ...f, bukti_path: data.path, bukti_url: data.url, bukti_filename: data.filename }));
      toast.success("Bukti terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal unggah"); }
    finally { setUploading(false); }
  };

  const submitPengajuan = async () => {
    if (!pengajuanForm.bukti_path) { toast.error("Unggah bukti transfer dulu"); return; }
    if (!pengajuanForm.rekening_id && rekenings.length > 0) { toast.error("Pilih rekening tujuan"); return; }
    if (!(pengajuanForm.amount > 0)) { toast.error("Nominal harus > 0"); return; }
    setSubmitting(true);
    try {
      const payload = {
        tagihan_id: pengajuanForm.tagihan_id || null,
        nomenklatur_code: pengajuanForm.nomenklatur_code,
        amount: Number(pengajuanForm.amount || 0),
        payment_date: pengajuanForm.payment_date,
        rekening_id: pengajuanForm.rekening_id,
        bukti_path: pengajuanForm.bukti_path,
        catatan: pengajuanForm.catatan || null,
      };
      if (pengajuanForm._editing_id) {
        await api.put(`/wali/pengajuan/${pengajuanForm._editing_id}`, payload, cfg);
        toast.success("Pengajuan diperbarui");
      } else {
        await api.post("/wali/pengajuan", payload, cfg);
        toast.success("Pengajuan terkirim — menunggu review admin");
      }
      setPengajuanOpen(false);
      await loadPengajuan();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const deletePengajuan = async (p) => {
    if (!confirm(`Hapus pengajuan ${formatIDR(p.amount)} tanggal ${formatDate(p.payment_date)}?`)) return;
    try {
      await api.delete(`/wali/pengajuan/${p.id}`, cfg);
      toast.success("Pengajuan dihapus");
      loadPengajuan();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const logout = () => {
    localStorage.removeItem("wali_token");
    localStorage.removeItem("wali_siswa");
    nav("/portal-wali/login");
  };

  const buildReceiptArgs = (p) => {
    const nomenklatur = { code: p.nomenklatur_code, name: p.nomenklatur_name };
    return { payment: p, siswa, lembaga, nomenklatur, settings, tagihan_sisa: -1 };
  };

  const printReceipt = async (p) => {
    try { await downloadReceipt(buildReceiptArgs(p)); toast.success("Kwitansi diunduh"); }
    catch (e) { toast.error("Gagal cetak: " + (e.message || "")); }
  };

  const shareWA = async (p) => {
    const url = buildWhatsAppReceiptURL(buildReceiptArgs(p));
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!siswa) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Memuat portal...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E6F7F7] via-white to-[#CCEEEF] dark:from-slate-900 dark:via-slate-900 dark:to-slate-800" data-testid="portal-wali-page">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#48B8BD] to-[#0B8D95] flex items-center justify-center">
              <Student size={18} weight="duotone" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#0B8D95] font-bold">Portal Wali Murid</div>
              <div className="font-display font-bold text-sm">{settings?.foundation_short_name || settings?.foundation_name || "EduFoundation"}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="wali-logout" className="gap-1.5"><SignOut size={14} />Keluar</Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Siswa card */}
        <div className="bg-gradient-to-br from-[#0B8D95] to-[#087178] rounded-2xl p-6 text-white shadow-lg" data-testid="wali-siswa-card">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-black">
              {siswa.name?.[0] || "?"}
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Siswa</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight" data-testid="wali-siswa-name">{siswa.name}</h1>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90">
                <span>NIS <b>{siswa.nis}</b></span>
                {siswa.nisn && <span>NISN <b>{siswa.nisn}</b></span>}
                {siswa.class_name && <span>Kelas <b>{siswa.class_name}</b></span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-90">
                {lembaga?.name && <span className="inline-flex items-center gap-1"><Buildings size={12} weight="duotone" /> {lembaga.name}</span>}
                {siswa.parent_name && <span>Wali: <b>{siswa.parent_name}</b></span>}
                {siswa.parent_phone && <span className="inline-flex items-center gap-1"><Phone size={12} weight="duotone" /> {siswa.parent_phone}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Ringkasan */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="wali-stat-tagihan">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total Tagihan</div>
              <Receipt size={18} weight="duotone" className="text-blue-500" />
            </div>
            <div className="font-display text-2xl font-black text-foreground tracking-tight">{formatIDR(tagihan?.total_tagihan || 0)}</div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="wali-stat-dibayar">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Sudah Dibayar</div>
              <CheckCircle size={18} weight="duotone" className="text-emerald-500" />
            </div>
            <div className="font-display text-2xl font-black text-emerald-600 tracking-tight">{formatIDR(tagihan?.total_dibayar || 0)}</div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="wali-stat-sisa">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Sisa Tagihan</div>
              <Wallet size={18} weight="duotone" className={(tagihan?.total_sisa || 0) > 0 ? "text-rose-500" : "text-slate-400"} />
            </div>
            <div className={`font-display text-2xl font-black tracking-tight ${(tagihan?.total_sisa || 0) > 0 ? "text-rose-600" : "text-foreground"}`}>{formatIDR(tagihan?.total_sisa || 0)}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border overflow-x-auto">
          {[{ k: "tagihan", label: "Tagihan" }, { k: "ajukan", label: "Ajukan Pembayaran" }, { k: "riwayat", label: "Riwayat Pembayaran" }, { k: "profil", label: "Profil Siswa" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.k ? "text-[#0B8D95] border-[#0B8D95]" : "text-muted-foreground border-transparent hover:text-foreground"}`}
              data-testid={`wali-tab-${t.k}`}>{t.label}
              {t.k === "ajukan" && pengajuans.filter(p => p.status === "pending").length > 0 && (
                <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pengajuans.filter(p => p.status === "pending").length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tagihan tab */}
        {tab === "tagihan" && (
          <div className="bg-card border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-bold">Periode</th>
                  <th className="px-4 py-3 font-bold">Nomenklatur</th>
                  <th className="px-4 py-3 font-bold">Jatuh Tempo</th>
                  <th className="px-4 py-3 font-bold text-right">Tagihan</th>
                  <th className="px-4 py-3 font-bold text-right">Dibayar</th>
                  <th className="px-4 py-3 font-bold text-right">Sisa</th>
                  <th className="px-4 py-3 font-bold text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {tagihan?.rows?.map((t) => (
                  <tr key={t.id} className="border-t border-border hover:bg-muted/30" data-testid={`wali-tagihan-${t.id}`}>
                    <td className="px-4 py-3 font-semibold">{formatMonth(t.period)}</td>
                    <td className="px-4 py-3"><div className="font-bold">{t.nomenklatur_code}</div><div className="text-xs text-muted-foreground">{t.nomenklatur_name || "-"}</div></td>
                    <td className="px-4 py-3 text-xs">{formatDate(t.due_date)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">{formatIDR(t.amount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(t.paid_amount)}</td>
                    <td className="px-4 py-3 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(t.sisa)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_META[t.status]?.cls || "bg-slate-100 text-slate-600"}`}>{STATUS_META[t.status]?.label || t.status?.toUpperCase()}</span>
                      {t.sisa > 0 && (
                        <button onClick={() => openPengajuan(t)} className="block mx-auto mt-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#0B8D95] text-white hover:bg-[#087178]" data-testid={`wali-ajukan-${t.id}`}>Bayar Sekarang</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!tagihan?.rows?.length && <tr><td colSpan={7} className="px-5 py-16 text-center text-muted-foreground">
                  <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
                  <div className="font-semibold">Belum ada tagihan tercatat</div>
                </td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Ajukan Pembayaran tab */}
        {tab === "ajukan" && (
          <div className="space-y-4">
            {/* Rekening Yayasan */}
            <div className="bg-card border border-border rounded-2xl p-5" data-testid="wali-rekening-list">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Rekening Tujuan Transfer</div>
                  <div className="text-xs text-muted-foreground">Transfer ke salah satu rekening di bawah, lalu ajukan pembayaran.</div>
                </div>
                <Button onClick={() => openPengajuan(null)} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="wali-pengajuan-add"><PaperPlaneTilt size={14} className="mr-1.5" />Ajukan Pembayaran</Button>
              </div>
              {rekenings.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">Rekening yayasan belum diatur. Hubungi admin.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {rekenings.map((r) => (
                    <div key={r.id} className="border border-border rounded-xl p-4 flex gap-3 items-start" data-testid={`wali-rekening-${r.id}`}>
                      <div className="h-10 w-10 rounded-lg bg-[#0B8D95]/10 flex items-center justify-center flex-shrink-0"><Bank size={18} weight="duotone" className="text-[#0B8D95]" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold">{r.bank_name}</div>
                        <div className="font-mono font-black text-lg tabular-nums select-all">{r.account_number}</div>
                        <div className="text-xs text-muted-foreground">a.n. {r.holder_name}</div>
                        {r.notes && <div className="text-xs italic text-muted-foreground mt-1">{r.notes}</div>}
                      </div>
                      {r.qris_image_path && (
                        <img src={`${API}/files/${r.qris_image_path}`} alt="QRIS" className="h-16 w-16 object-contain border border-border rounded" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Riwayat pengajuan */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Riwayat Pengajuan</div>
                <div className="text-xs text-muted-foreground">Pantau status pengajuan pembayaran Anda di sini.</div>
              </div>
              {pengajuans.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">Belum ada pengajuan. Klik "Ajukan Pembayaran" di atas untuk mulai.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-bold">Tanggal</th>
                      <th className="px-4 py-3 font-bold">Nomen.</th>
                      <th className="px-4 py-3 font-bold text-right">Jumlah</th>
                      <th className="px-4 py-3 font-bold">Rekening</th>
                      <th className="px-4 py-3 font-bold">Bukti</th>
                      <th className="px-4 py-3 font-bold text-center">Status</th>
                      <th className="px-4 py-3 font-bold text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pengajuans.map((p) => {
                      const S = PENGAJUAN_STATUS[p.status] || PENGAJUAN_STATUS.pending;
                      return (
                        <tr key={p.id} className="border-t border-border" data-testid={`wali-pengajuan-${p.id}`}>
                          <td className="px-4 py-3 text-xs">{formatDate(p.payment_date)}</td>
                          <td className="px-4 py-3"><div className="font-bold text-xs">{p.nomenklatur_code}</div><div className="text-[10px] text-muted-foreground">{p.nomenklatur_name}</div></td>
                          <td className="px-4 py-3 text-right font-mono font-bold">{formatIDR(p.amount)}</td>
                          <td className="px-4 py-3 text-xs">{p.rekening_label || "-"}</td>
                          <td className="px-4 py-3"><a href={`${API}/files/${p.bukti_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">Lihat</a></td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black border ${S.cls}`}><S.icon size={10} weight="fill" />{S.label}</span>
                            {p.rejection_reason && <div className="text-[10px] text-rose-600 mt-1 italic">"{p.rejection_reason}"</div>}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {p.status === "pending" && (
                              <>
                                <button onClick={() => openEditPengajuan(p)} className="p-1 hover:bg-blue-50 rounded text-blue-600" title="Edit" data-testid={`wali-pengajuan-edit-${p.id}`}><PencilSimple size={14} /></button>
                                <button onClick={() => deletePengajuan(p)} className="p-1 hover:bg-rose-50 rounded text-rose-600" title="Hapus" data-testid={`wali-pengajuan-delete-${p.id}`}><Trash size={14} /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Riwayat tab */}
        {tab === "riwayat" && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="p-3 bg-muted/30 text-xs flex justify-between">
              <span>Total {pembayaran?.count || 0} transaksi</span>
              <span className="font-black font-mono tabular-nums text-emerald-600">{formatIDR(pembayaran?.total || 0)}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-bold">Tanggal</th>
                  <th className="px-4 py-3 font-bold">No. Kwitansi</th>
                  <th className="px-4 py-3 font-bold">Nomenklatur</th>
                  <th className="px-4 py-3 font-bold">Metode</th>
                  <th className="px-4 py-3 font-bold text-right">Jumlah</th>
                  <th className="px-4 py-3 font-bold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pembayaran?.rows?.map((p) => (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/30" data-testid={`wali-pembayaran-${p.id}`}>
                    <td className="px-4 py-3 font-semibold">{formatDate(p.payment_date)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.receipt_no || "-"}</td>
                    <td className="px-4 py-3"><div className="font-bold">{p.nomenklatur_code}</div><div className="text-xs text-muted-foreground">{p.nomenklatur_name || "-"}</div></td>
                    <td className="px-4 py-3 text-xs uppercase">{p.method || "-"}</td>
                    <td className="px-4 py-3 text-right font-mono font-black tabular-nums">{formatIDR(p.amount)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => printReceipt(p)} className="p-1.5 hover:bg-[#0B8D95]/10 rounded text-[#0B8D95]" title="Cetak Kwitansi" data-testid={`wali-print-${p.id}`}><Printer size={14} weight="duotone" /></button>
                      <button onClick={() => shareWA(p)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Kirim WA" data-testid={`wali-wa-${p.id}`}><WhatsappLogo size={14} weight="fill" /></button>
                    </td>
                  </tr>
                ))}
                {!pembayaran?.rows?.length && <tr><td colSpan={6} className="px-5 py-16 text-center text-muted-foreground">Belum ada riwayat pembayaran</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Profil tab */}
        {tab === "profil" && (
          <div className="bg-card border border-border rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {[
              ["NIS", siswa.nis], ["NISN", siswa.nisn || "-"],
              ["Nama Lengkap", siswa.name], ["Jenis Kelamin", siswa.gender === "L" ? "Laki-laki" : (siswa.gender === "P" ? "Perempuan" : "-")],
              ["Tempat, Tanggal Lahir", `${siswa.birth_place || "-"}, ${siswa.birth_date ? formatDate(siswa.birth_date) : "-"}`],
              ["Kelas", siswa.class_name || "-"],
              ["Lembaga", lembaga?.name || "-"],
              ["Status", (siswa.status || "-").toUpperCase()],
              ["Nama Wali", siswa.parent_name || "-"],
              ["No HP Wali", siswa.parent_phone || "-"],
              ["Alamat", siswa.address || "-"],
            ].map(([label, value], i) => (
              <div key={i} className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</span>
                <span className="text-sm font-semibold text-foreground mt-0.5">{value}</span>
              </div>
            ))}
            {lembaga?.phone && (
              <div className="col-span-full mt-4 pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Kontak lembaga:</div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#0B8D95]" /> {lembaga.phone}</span>
                  {lembaga.address && <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#0B8D95]" /> {lembaga.address}</span>}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-muted-foreground py-6">
        © {new Date().getFullYear()} {settings?.foundation_name || "EduFoundation"} · Portal Wali Murid
      </footer>

      {/* Ajukan Pembayaran Dialog */}
      <Dialog open={pengajuanOpen} onOpenChange={(v) => { if (!submitting) setPengajuanOpen(v); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="wali-pengajuan-dialog">
          <DialogHeader><DialogTitle>{pengajuanForm._editing_id ? "Edit Pengajuan" : "Ajukan Pembayaran"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomen. *</Label>
                <Select value={pengajuanForm.nomenklatur_code} onValueChange={(v) => setPengajuanForm({ ...pengajuanForm, nomenklatur_code: v })}>
                  <SelectTrigger className="bg-white" data-testid="wali-pengajuan-nomen"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["IPP","SPP","DKG","DPR","DSR","DHF","DKL","PSB","AJP","DBK"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal Bayar *</Label><Input type="date" value={pengajuanForm.payment_date || ""} onChange={(e) => setPengajuanForm({ ...pengajuanForm, payment_date: e.target.value })} data-testid="wali-pengajuan-date" /></div>
            </div>
            <div><Label>Jumlah (Rp) *</Label><Input type="number" value={pengajuanForm.amount || 0} onChange={(e) => setPengajuanForm({ ...pengajuanForm, amount: Number(e.target.value) })} data-testid="wali-pengajuan-amount" /></div>
            <div><Label>Rekening Tujuan {rekenings.length > 0 && "*"}</Label>
              {rekenings.length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="wali-no-rekening">Rekening yayasan belum diatur. Silakan transfer sesuai arahan sekolah dan sebutkan rekening tujuan di catatan.</div>
              ) : (
              <Select value={pengajuanForm.rekening_id} onValueChange={(v) => setPengajuanForm({ ...pengajuanForm, rekening_id: v })}>
                <SelectTrigger className="bg-white" data-testid="wali-pengajuan-rekening"><SelectValue placeholder="Pilih rekening" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {rekenings.map((r) => <SelectItem key={r.id} value={r.id}>{r.bank_name} · {r.account_number}</SelectItem>)}
                </SelectContent>
              </Select>
              )}
            </div>
            <div>
              <Label>Bukti Transfer * (JPG/PNG/PDF, max 1 MB)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" hidden onChange={(e) => e.target.files?.[0] && uploadBukti(e.target.files[0])} data-testid="wali-pengajuan-file" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} type="button"><UploadSimple size={14} className="mr-1.5" />{uploading ? "Mengunggah..." : (pengajuanForm.bukti_path ? "Ganti Bukti" : "Unggah Bukti")}</Button>
                {pengajuanForm.bukti_path && <a href={pengajuanForm.bukti_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">{pengajuanForm.bukti_filename || "bukti"}</a>}
              </div>
            </div>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={pengajuanForm.catatan || ""} onChange={(e) => setPengajuanForm({ ...pengajuanForm, catatan: e.target.value })} placeholder="Contoh: pembayaran SPP September" data-testid="wali-pengajuan-catatan" /></div>
            <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
              💡 Setelah pengajuan dikirim, tunggu admin lembaga meng-approve. Kalau disetujui, tagihan otomatis berkurang & kwitansi bisa dicetak.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPengajuanOpen(false)} disabled={submitting}>Batal</Button>
            <Button onClick={submitPengajuan} disabled={submitting || uploading || !pengajuanForm.bukti_path} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="wali-pengajuan-submit">{submitting ? "Mengirim..." : "Kirim Pengajuan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
